import { SiGoogle, SiMeta } from "@icons-pack/react-simple-icons";
import { cn } from "@nexus/ui";

/**
 * Brand marks keyed by the ids `/mock-data/ad-platforms` serves — lowercase (`meta`,
 * `google`), a separate vocabulary from preflight's uppercase `PreflightPlatformCode`,
 * so this map stays distinct from `PLATFORM_BRAND_ICONS`.
 */
const AD_PLATFORM_BRAND_ICONS = {
  // Matches the Meta blue preflight already paints, so one brand never gets two blues.
  meta: { Glyph: SiMeta, colorClassName: "text-[#0467DF]" },
  google: { Glyph: SiGoogle, colorClassName: "text-[#4285F4]" },
} as const;

export function hasAdPlatformBrandIcon(platformId: string): boolean {
  return platformId in AD_PLATFORM_BRAND_ICONS;
}

export interface AdPlatformIconProps {
  platformId: string;
  className?: string;
}

/**
 * A platform's brand mark in its brand colour. Renders nothing for an unrecognised id
 * rather than inventing a logo, so a new server-side platform degrades to a bare label.
 */
export function AdPlatformIcon({ platformId, className }: AdPlatformIconProps) {
  const brand =
    AD_PLATFORM_BRAND_ICONS[platformId as keyof typeof AD_PLATFORM_BRAND_ICONS];
  if (!brand) return null;

  const { Glyph, colorClassName } = brand;
  return (
    <Glyph
      aria-hidden
      focusable={false}
      // simple-icons renders `<title>{brand}</title>` unless silenced, and Select mirrors
      // item content into its trigger — that would print the name twice next to the label.
      title=""
      className={cn("size-3.5 shrink-0", colorClassName, className)}
    />
  );
}
